import {
  Alert,
  Box,
  Button,
  Divider,
  Link,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import iconUrl from '../assets/gg-black.svg';
import backgroundUrl from '../assets/gg-signin-background.jpg';
import googleMarkUrl from '../assets/google-g.svg';
import {
  cancelOtpChallenge,
  getGoogleSignInUrl,
  OTP_CODE_LENGTH,
  startSignIn,
  submitOtpCode,
  useAuthStore,
} from '../utils/auth';

// LoginCallback redirects here with a `login_error` reason when the Google
// flow cannot finish. The three cases need different advice: a rejected
// account is permanent and needs an administrator, an unverifiable callback
// means starting over in this browser, and everything else is worth retrying.
const LOGIN_ERRORS: Record<string, string> = {
  account:
    'This user account does not exist. Please contact your administrator.',
  session:
    "We couldn't verify that sign-in attempt. Please start again from this page.",
  failed: "Google sign in didn't complete. Please try again.",
};

function Login() {
  const navigate = useNavigate();
  const session = useAuthStore((s) => s.session);
  const loading = useAuthStore((s) => s.loading);
  const error = useAuthStore((s) => s.error);
  const needsOtpCode = useAuthStore((s) => s.needsOtpCode);

  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [loginError, setLoginError] = useState<string | null>(null);

  useEffect(() => {
    if (session) {
      navigate('/', { replace: true });
    }
  }, [session, navigate]);

  useEffect(() => {
    const reason = new URLSearchParams(window.location.search).get(
      'login_error',
    );
    if (!reason) return;
    setLoginError(LOGIN_ERRORS[reason] ?? LOGIN_ERRORS.failed);
  }, []);

  async function handleEmailSubmit(e: React.FormEvent) {
    e.preventDefault();
    try {
      await startSignIn(email);
    } catch {
      // Error is in the store; useAuthStore subscriber renders it.
    }
  }

  async function handleOtpSubmit(e: React.FormEvent) {
    e.preventDefault();
    try {
      await submitOtpCode(code);
    } catch {
      // Error is in the store.
    }
  }

  // Requesting a new code invalidates whatever is already typed, and this is
  // the recovery path from an unusable challenge session, so the field starts
  // empty rather than holding a code that can no longer verify.
  async function handleResend() {
    setCode('');
    try {
      await startSignIn(email);
    } catch {
      // Error is in the store.
    }
  }

  // The email field empties too, so the address the user just chose to leave
  // isn't sitting in the box they have to type the next one into.
  function handleReset() {
    setCode('');
    setEmail('');
    cancelOtpChallenge();
  }

  // Building the authorize URL can fail before any redirect happens:
  // sessionStorage throws where the browser blocks storage, and crypto.subtle
  // is undefined on a non-secure origin. Both raise browser-internal text that
  // tells the user nothing, so the console keeps the detail and the alert names
  // the step that still works.
  async function handleGoogle() {
    useAuthStore.setState({ error: null });
    try {
      window.location.href = await getGoogleSignInUrl();
    } catch (err) {
      console.error('Google sign in could not start:', err);
      useAuthStore.setState({
        error:
          "Google sign in couldn't start in this browser. Sign in with an email code instead.",
      });
    }
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
        py: 4,
        backgroundImage: `url(${backgroundUrl})`,
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2,
          width: '100%',
          maxWidth: 400,
        }}
      >
        <img src={iconUrl} alt="Grant Guardian" height={70} />

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
            width: '100%',
            p: 4,
            // The card sits on a photograph, so it carries its own surface
            // color; without one the form reads against the image.
            bgcolor: 'background.paper',
            border: '2px solid #231f20',
            borderRadius: 2,
          }}
        >
          {loginError && (
            <Alert severity="warning" role="alert" aria-live="polite">
              {loginError}
            </Alert>
          )}

          {needsOtpCode ? (
            <>
              <Typography variant="h5">Enter your code</Typography>
              <Typography variant="body2" color="text.secondary">
                We sent a {OTP_CODE_LENGTH}-digit code to {email}.
              </Typography>
              <Box
                component="form"
                onSubmit={handleOtpSubmit}
                sx={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                }}
              >
                <TextField
                  label={`${OTP_CODE_LENGTH}-digit code`}
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  autoFocus
                  required
                  slotProps={{
                    htmlInput: {
                      inputMode: 'numeric',
                      pattern: '[0-9]*',
                      maxLength: OTP_CODE_LENGTH,
                    },
                  }}
                />
                {error && <Alert severity="error">{error}</Alert>}
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading || code.length < OTP_CODE_LENGTH}
                >
                  {loading ? 'Verifying…' : 'Verify'}
                </Button>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Link component="button" type="button" onClick={handleResend}>
                    Resend code
                  </Link>
                  <Link component="button" type="button" onClick={handleReset}>
                    Use a different email
                  </Link>
                </Box>
              </Box>
            </>
          ) : (
            <>
              <Typography variant="h5">Sign in to Grant Guardian</Typography>
              <Box
                component="form"
                onSubmit={handleEmailSubmit}
                sx={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                }}
              >
                <TextField
                  label="Email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoFocus
                  required
                  autoComplete="email"
                />
                {error && <Alert severity="error">{error}</Alert>}
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading || !email}
                >
                  {loading ? 'Sending code…' : 'Send code'}
                </Button>
              </Box>

              <Divider sx={{ width: '100%' }}>or</Divider>

              <Button
                variant="outlined"
                fullWidth
                onClick={handleGoogle}
                aria-label="Continue with Google"
                startIcon={
                  <img src={googleMarkUrl} alt="" width={20} height={20} />
                }
                sx={{
                  py: 1.25,
                  // The icon slot is a text box by default, so the mark rides
                  // the label's baseline and lands below the center of the
                  // words next to it. Sizing the slot to the mark itself
                  // leaves the button's own centering to place it.
                  '& .MuiButton-startIcon': {
                    display: 'flex',
                    alignItems: 'center',
                  },
                  color: '#231f20',
                  bgcolor: 'background.paper',
                  // The theme's outlined variant is a 3px inherit-colored rule,
                  // which reads as a second black card edge next to the Google
                  // mark. Google's own button is a hairline gray.
                  borderWidth: '1px',
                  borderColor: '#d4d4d4',
                  '&:hover': {
                    borderWidth: '1px',
                    borderColor: '#a3a3a3',
                    bgcolor: '#f5f5f5',
                  },
                }}
              >
                Continue with Google
              </Button>
            </>
          )}
        </Box>

        {/* App.tsx routes every unmatched logged-out path here, so visitors
            following a stale or mistyped link arrive at a sign-in form they
            may have no account for. These are the destinations reachable
            without one. */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Button
            href="/request-access/"
            aria-label="Request Access for Your Foundation"
          >
            Request Access for Your Foundation
          </Button>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            <Button href="/" aria-label="Go to the Grant Guardian home page">
              Home
            </Button>
            <Button
              href="https://www.mcgovern.org/our-work/data-solutions/grant-guardian/"
              aria-label="Visit PJMF's Grant Guardian product page"
            >
              Learn More
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default Login;
